import { useCallback, useRef, useState, type RefObject } from "react";
import { Entity } from "@common/types/entity";
import { fetchInvoiceTemplate } from "@api/invoiceTemplate.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { createNativeInvoiceFormStateFromTemplate } from "./createNativeInvoiceFormStateFromTemplate";
import type { CreateNativeInvoiceFormStateResult } from "./createNativeInvoiceFormState";

interface Params {
    workspaceId: string | undefined;
    entityId: number;
    selectedEntity: Entity;
    entities: Entity[];
    primaryAccrualLedgerId?: string;
    incomeAccountLedgerId?: string;
    initialTemplateId?: string;
    applyFormState: (next: CreateNativeInvoiceFormStateResult) => void;
    // A template is entity-scoped, so applying one from another entity also
    // switches the invoice's From entity. When the entity is locked (existing
    // invoice, billable expenses, catalog lines) such a template can't be
    // applied at all.
    isFromEntityLocked: boolean;
    onTemplateEntitySwitch: (entityId: number) => void;
    // True when the draft still looks like a freshly-opened, untouched form
    // (see isNativeInvoiceFormStateEmpty) — NOT the form's dirty flag. A
    // draft populated by a previously-applied template that the user hasn't
    // touched since is non-empty but not dirty, and must still be confirmed
    // before a different template overwrites it.
    isFormEmptyRef: RefObject<boolean>;
    // Content the user typed: a template swap would replace it, so it
    // confirms first.
    isDirtyRef: RefObject<boolean>;
}

export function useApplyInvoiceTemplate({
    workspaceId,
    entityId,
    selectedEntity,
    entities,
    primaryAccrualLedgerId,
    incomeAccountLedgerId,
    initialTemplateId,
    applyFormState,
    isFromEntityLocked,
    onTemplateEntitySwitch,
    isFormEmptyRef,
    isDirtyRef,
}: Params) {
    const { error: toastError } = useToaster();
    const requestIdRef = useRef(0);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
        initialTemplateId ?? null,
    );
    const [appliedTemplateId, setAppliedTemplateId] = useState<
        string | undefined
    >(initialTemplateId);
    const [pendingTemplate, setPendingTemplate] = useState<{
        templateId: string;
        templateEntityId: number;
    } | null>(null);
    const [isApplying, setIsApplying] = useState(false);

    // Callers own the "already applied" check: a template switch skips a no-op
    // re-apply.
    const applyTemplate = useCallback(
        async (templateId: string, templateEntityId?: number) => {
            if (!workspaceId) {
                return;
            }

            const requestId = ++requestIdRef.current;
            const previousSelectedId = selectedTemplateId;
            setSelectedTemplateId(templateId);
            setIsApplying(true);

            try {
                // Templates are entity-scoped on the backend; a template picked
                // from another entity must be fetched under its own entity.
                const detail = await fetchInvoiceTemplate({
                    workspaceId,
                    entityId: templateEntityId ?? entityId,
                    templateId,
                });
                if (requestId !== requestIdRef.current) {
                    return;
                }
                const templateEntity =
                    entities.find((entity) => entity.id === templateEntityId) ??
                    selectedEntity;
                applyFormState(
                    createNativeInvoiceFormStateFromTemplate({
                        detail,
                        entity: templateEntity,
                        primaryAccrualLedgerId,
                        incomeAccountLedgerId,
                    }),
                );
                // A cross-entity template drags the From entity with it — an
                // invoice must never mix one entity's template with another
                // entity's From.
                if (templateEntity.id !== selectedEntity.id) {
                    onTemplateEntitySwitch(templateEntity.id);
                }
                setAppliedTemplateId(templateId);
            } catch {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setSelectedTemplateId(previousSelectedId);
                toastError("Couldn't load this template");
            } finally {
                if (requestId === requestIdRef.current) {
                    setIsApplying(false);
                }
            }
        },
        [
            applyFormState,
            entities,
            entityId,
            onTemplateEntitySwitch,
            primaryAccrualLedgerId,
            incomeAccountLedgerId,
            selectedEntity,
            selectedTemplateId,
            toastError,
            workspaceId,
        ],
    );

    const requestApplyTemplate = useCallback(
        (templateId: string, templateEntityId: number) => {
            if (templateId === appliedTemplateId) {
                return;
            }
            if (isFromEntityLocked && templateEntityId !== selectedEntity.id) {
                toastError(
                    "This template belongs to another entity, and this invoice's entity can't be changed",
                );
                return;
            }
            // Only real work is worth a confirmation. Content the user typed is
            // dirty; content that arrived with a previously applied template is
            // not (the baseline folds it in), and a template swap replaces it
            // wholesale anyway. Before any template is applied, a non-empty
            // draft is prefilled content (e.g. billable expenses) that a
            // template would silently wipe, so that still confirms.
            const hasWorkToLose =
                isDirtyRef.current ||
                (!appliedTemplateId && !isFormEmptyRef.current);
            if (hasWorkToLose) {
                setPendingTemplate({ templateId, templateEntityId });
                return;
            }
            void applyTemplate(templateId, templateEntityId);
        },
        [
            appliedTemplateId,
            applyTemplate,
            isDirtyRef,
            isFormEmptyRef,
            isFromEntityLocked,
            selectedEntity.id,
            toastError,
        ],
    );

    const confirmPendingTemplate = useCallback(() => {
        if (!pendingTemplate) {
            return;
        }
        const { templateId, templateEntityId } = pendingTemplate;
        setPendingTemplate(null);
        void applyTemplate(templateId, templateEntityId);
    }, [applyTemplate, pendingTemplate]);

    const cancelPendingTemplate = useCallback(() => {
        setPendingTemplate(null);
    }, []);

    // Disconnects the draft from its template (e.g. when the user manually
    // switches the From entity away from the template's): the picker empties,
    // the save stops recording a template backlink, and any in-flight apply
    // is dropped.
    const clearTemplateSelection = useCallback(() => {
        requestIdRef.current++;
        setSelectedTemplateId(null);
        setAppliedTemplateId(undefined);
        setPendingTemplate(null);
        setIsApplying(false);
    }, []);

    return {
        selectedTemplateId,
        appliedTemplateId,
        isApplying,
        isConfirmOpen: pendingTemplate != null,
        applyTemplateImmediately: applyTemplate,
        requestApplyTemplate,
        confirmPendingTemplate,
        cancelPendingTemplate,
        clearTemplateSelection,
    };
}
